import { XIcon } from 'assests/GmsIcons';
import React, { useMemo, useState, useCallback, useEffect, useContext, useRef } from 'react';
import { ENTER_NAME } from '../ISR/ISRConstants';
import { type StylesConfig } from "react-select";
import { debounce } from 'lodash';
import { AppContext } from 'Context/AppContext';
import { getUserList } from './services/RuleService';
import Select from "react-select";

export type User = {
    first_name: string | null;
    last_name: string | null;
    id: string;
    industry_id: string;
    is_deleted: boolean;
    party_id: string;
}

type ChangeOwnerDialogProps = {
    dialogHeader: string;
    labelName: string;
    value?: User;
    showCancelButton?: boolean;
    actionLabel?: string;
    onClose: () => void;
    onSubmit: (user: User) => void;
    showIndicator?: boolean;
}

const ChangeOwnerDialog = ({
    dialogHeader = "",
    labelName = "",
    value,
    onClose,
    onSubmit,
    showCancelButton = true,
    actionLabel,
    showIndicator = false,
}: ChangeOwnerDialogProps) => {
    const [user, setUser] = useState<User | null>(value ?? null);
    const [loading, setLoading] = useState(false);
    const [hasMoreSearch, setHasMoreSearch] = useState(true);
    const [hasMoreNormal, setHasMoreNormal] = useState(true);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [normalPage, setNormalPage] = useState(1);
    const [searchPage, setSearchPage] = useState(1);
    const loadedPagesRef = useRef<Set<number>>(new Set([1]));
    const [isSearching, setIsSearching] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const isMounted = useRef(true);
    const limit = 10;

    const appContext = useContext(AppContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.sams_id;
    const partyId = appContext?.get_selected_party()?.sams_id;

    const fetchUsers = useCallback(async (pageNum: number, search: string) => {
        if (!partyId || !industryId || !isMounted.current) return;

        setLoading(true);
        try {
            const response = await getUserList(
                partyId,
                industryId,
                (pageNum - 1) * limit,
                limit,
                search
            );

            const totalCount = parseInt(response?.data?.data?.total_count || '0');
            const newUsers = response?.data?.data?.users || [];
            const currentCount = (pageNum - 1) * limit + newUsers.length;

            if (search) {
                setFilteredUsers(prev =>
                    pageNum === 1 ? newUsers : [...prev, ...newUsers]
                );
                setSearchPage(pageNum);
                setHasMoreSearch(currentCount < totalCount);
            } else {
                setAllUsers(prev => {
                    const uniqueValues = [...prev];
                    newUsers.forEach((newVal: User) => {
                        if (!uniqueValues.some(existing => existing.id === newVal.id)) {
                            uniqueValues.push(newVal);
                        }
                    });
                    return uniqueValues;
                });
                loadedPagesRef.current.add(pageNum);
                setNormalPage(prevPage => Math.max(prevPage, pageNum));
                setHasMoreNormal(currentCount < totalCount);
            }
        } catch (error) {
            console.error("Error fetching users", error);
        } finally {
            setLoading(false);
        }
    }, [industryId, partyId, limit]);

    const customStyles: StylesConfig<User, false> = {
        control: (provided, state) => ({
            ...provided,
            border: `1px solid ${state.isFocused ? '#0747a6' : '#DADBDD'}`,
            boxShadow: state.isFocused ? '0 0 0 1px #0747a6' : 'none',
            '&:hover': {
                border: `1px solid ${state.isFocused ? '#0747a6' : '#DADBDD'}`
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: 'transparent',
            background: state.isSelected && state.isFocused
                ? '#0747a6'
                : state.isSelected
                    ? '#0747a6'
                    : state.isFocused
                        ? '#F4F5F7'
                        : 'white',
            color: state.isSelected ? 'white' : '#172B4D',
            cursor: 'pointer',
            '&:active': {
                backgroundColor: state.isSelected ? '#0747a6' : '#F4F5F7'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        })
    };

    const selectProps = useMemo(() => ({
        id: "usersId",
        className: "tw-outline-none",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: '#0747a6'
            })
        },
        isSearchable: true,
        maxMenuHeight: 220,
        menuPlacement: "bottom" as const,
        menuPosition: "absolute" as const,
        isClearable: false,
        placeholder: ENTER_NAME,
        isLoading: loading,
        getOptionLabel: (option: User) => {
            const firstName = option?.first_name || '';
            const lastName = option?.last_name || '';
            return `${firstName} ${lastName}`.trim() || 'Unnamed User';
        }
    }), [loading]);
    useEffect(() => {
        if (!isSearching && allUsers.length === 0) {
            fetchUsers(1, "");
        }
    }, [fetchUsers, isSearching]);
    const handleMenuScrollToBottom = useCallback(() => {
        if (isSearching && hasMoreSearch) {
            const nextSearchPage = searchPage + 1
            fetchUsers(nextSearchPage, searchValue)
        } else if (!isSearching && hasMoreNormal) {
            const nextNormalPage = normalPage + 1
            if (!loadedPagesRef.current.has(nextNormalPage)) {
                fetchUsers(nextNormalPage, "")
            }
        }
    }, [
        isSearching,
        hasMoreSearch,
        hasMoreNormal,
        searchPage,
        normalPage,
        searchValue,
        fetchUsers
    ])

    const handleConfirmClick = () => {
        if (user == null) return;
        onSubmit(user);
    };

    const handleFieldChange = (newValue: User | null) => {
        setUser(newValue ?? null);
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((input: string) => {
                setIsSearching(true);
                setSearchPage(1);
                setHasMoreSearch(true);
                setFilteredUsers([]);
                fetchUsers(1, input);
            }, 300),
        [fetchUsers]
    );

    const debouncedLoadMoreOptions = useCallback(
        debounce(() => {
            const nextPage = isSearching ? searchPage + 1 : normalPage + 1;

            if (isSearching && hasMoreSearch) {
                fetchUsers(nextPage, searchValue);
            } else if (!isSearching && hasMoreNormal && !loadedPagesRef.current.has(nextPage)) {
                fetchUsers(nextPage, "");
            }
        }, 300),
        [hasMoreNormal, hasMoreSearch, normalPage, searchPage, isSearching, searchValue]
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            debouncedSearch.cancel();
            debouncedLoadMoreOptions.cancel();
            setSearchValue("");
            setNormalPage(1);
            setSearchPage(1);
            setHasMoreNormal(true);
            setHasMoreSearch(true);
        };
    }, []);



    const resetSearchState = useCallback(() => {
        setFilteredUsers([])
        setSearchPage(1)
        setHasMoreSearch(true)
        setIsSearching(false)
        setSearchValue("")
    }, [])

    const handleInputChange = useCallback(
        (input: string, { action }: { action: string }) => {
            if (action === "input-blur" || action === "menu-close" || action === "set-value") return;

            if (input === "") {
                resetSearchState();
                fetchUsers(1, "");
                return;
            }

            setSearchValue(input);
            debouncedSearch(input);
        },
        [fetchUsers, resetSearchState, debouncedSearch]
    );

    const handleMenuClose = useCallback(() => {
        if (!searchValue) return

        resetSearchState()
        if (allUsers.length === 0) {
            fetchUsers(normalPage, "")
        }
    }, [searchValue, normalPage, resetSearchState, allUsers?.length])

    useEffect(() => {
        return () => {
            isMounted.current = false
            resetSearchState()
            setNormalPage(1)
            loadedPagesRef.current = new Set([1])
        }
    }, [resetSearchState])

    return (
        <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-800 tw-bg-opacity-50 tw-z-50">
            <div className="tw-bg-white tw-rounded tw-shadow-md tw-w-1/4 tw-h-auto">
                <div className="tw-flex tw-justify-between tw-items-center">
                    <div className="tw-flex tw-items-center tw-gap-10">
                        <h2 className="tw-text-sm tw-font-semibold tw-my-3 tw-px-3">{dialogHeader}</h2>
                    </div>
                    <button className="tw-mr-2" onClick={onClose}><XIcon /></button>
                </div>

                <div className="tw-box-border tw-w-auto tw-h-px tw-border-t-[1px] tw-border-solid tw-border-[#D1D1D6]" />
                <div className="tw-px-3 tw-my-3">
                    <label className="tw-font-normal tw-w-full tw-text-sm tw-ps-1">{labelName}</label>
                    <div className="tw-relative tw-mt-2">
                        <Select<User>
                            {...selectProps}
                            options={isSearching ? filteredUsers : allUsers}
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            onInputChange={handleInputChange}
                            onChange={(selectedOption) => {
                                if (selectedOption) {
                                    handleFieldChange(selectedOption)
                                }
                            }}
                            onMenuClose={handleMenuClose}
                            value={user}
                            isMulti={false}
                            getOptionValue={(option: User) => option.id}
                            components={{
                                IndicatorSeparator: () => null,
                                NoOptionsMessage: () => (
                                    <div className="tw-p-2 tw-text-center tw-text-gray-500">
                                        No users available
                                    </div>
                                )
                            }}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-justify-between tw-item-center tw-px-2 tw-py-3">
                    <div className="tw-mb-3 tw-ms-2">
                        {showIndicator ? <label className="tw-text-[#747593] tw-text-sm">Loading...</label> : null}
                    </div>
                    <div className="tw-flex tw-gap-2 tw-justify-end">
                        {showCancelButton ? <button onClick={onClose} className="samll-outline-btn">Cancel</button> : null}
                        <button
                            disabled={user == null}
                            onClick={handleConfirmClick}
                            className="small-filled-btn"
                        >
                            {actionLabel ?? 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeOwnerDialog;